import { z } from '@hono/zod-openapi';
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const footballTeamTypeSchema = z.enum(['club', 'national', 'representative', 'unknown']);
export const playerTeamRelationshipTypeSchema = z.enum([
    'club_registration',
    'loan',
    'national_selection',
    'youth_registration',
    'unknown',
]);
export const playerTeamAssertionStateSchema = z.enum(['observed', 'inferred', 'confirmed']);
export const playerTeamEvidenceStrengthSchema = z.enum(['strong', 'supported', 'unsupported']);
export const footballPlayerSummarySchema = z.object({
    id: z.string().min(1),
    apiSportsPlayerId: z.number().int().positive(),
    displayName: z.string().min(1),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    birthDate: isoDateSchema.nullable(),
    nationality: z.string().nullable(),
    photoUrl: z.string().url().nullable(),
});
export const footballTeamSummarySchema = z.object({
    id: z.string().min(1),
    apiSportsTeamId: z.number().int().positive(),
    displayName: z.string().min(1),
    teamType: footballTeamTypeSchema,
    country: z.string().nullable(),
    logoUrl: z.string().url().nullable(),
});
export const playerTeamMembershipSchema = z.object({
    membershipId: z.string().min(1),
    relationshipType: playerTeamRelationshipTypeSchema,
    assertionState: playerTeamAssertionStateSchema,
    validFrom: isoDateSchema,
    validTo: isoDateSchema.nullable(),
    evidence: z.object({
        strength: playerTeamEvidenceStrengthSchema,
        observationCount: z.number().int().nonnegative(),
        transferBoundaryCount: z.number().int().min(0).max(2),
    }),
    team: footballTeamSummarySchema,
});
const projectedTeamReferenceSchema = z.object({
    teamApiSportsId: z.number().int().positive(),
    teamDisplayName: z.string().min(1),
    teamType: footballTeamTypeSchema,
    teamLogoUrl: z.string().url().nullable(),
});
export const playerTeamObservationSchema = projectedTeamReferenceSchema.extend({
    observationId: z.string().min(1),
    rawPayloadArchiveId: z.string().min(1),
    sourceEndpoint: z.string().min(1),
    sourceKey: z.string().min(1),
    observationType: z.enum(['squad', 'lineup', 'fixture_statistics', 'season_statistics', 'transfer']),
    observedAt: z.iso.datetime({ offset: true }),
    apiSportsFixtureId: z.number().int().positive().nullable(),
    competitionCode: z.string().nullable(),
    providerSeason: z.number().int().positive().nullable(),
    details: z.record(z.string(), z.unknown()),
});
export const fixturePlayerAppearanceSchema = projectedTeamReferenceSchema.extend({
    appearanceId: z.string().min(1),
    apiSportsFixtureId: z.number().int().positive(),
    lineupStatus: z.enum(['starter', 'substitute', 'appeared', 'roster', 'unknown']),
    minutes: z.number().int().nonnegative().nullable(),
    jerseyNumber: z.number().int().nonnegative().nullable(),
    position: z.string().nullable(),
    rating: z.string().nullable(),
    captain: z.boolean().nullable(),
});
export const footballPlayerRelationshipParamsSchema = z.object({
    apiSportsPlayerId: z.coerce.number().int().positive(),
});
export const footballPlayerRelationshipQuerySchema = z.object({
    at: isoDateSchema.optional(),
    observationLimit: z.coerce.number().int().min(1).max(100).default(20),
    observationOffset: z.coerce.number().int().nonnegative().default(0),
    appearanceLimit: z.coerce.number().int().min(1).max(100).default(20),
    appearanceOffset: z.coerce.number().int().nonnegative().default(0),
});
const relationshipPageSchema = z.object({
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    hasMore: z.boolean(),
});
const membershipConflictPartySchema = z.object({
    membershipId: z.string().min(1),
    relationshipType: playerTeamRelationshipTypeSchema,
    team: footballTeamSummarySchema,
});
const membershipConflictSchema = z.object({
    conflictType: z.literal('overlapping_club_memberships'),
    validFrom: isoDateSchema,
    validTo: isoDateSchema.nullable(),
    left: membershipConflictPartySchema,
    right: membershipConflictPartySchema,
});
const relationshipSummarySchema = z.object({
    confirmedMemberships: z.number().int().nonnegative(),
    observedMemberships: z.number().int().nonnegative(),
    inferredMemberships: z.number().int().nonnegative(),
    observedAssociations: z.number().int().nonnegative(),
    strongEvidenceMemberships: z.number().int().nonnegative(),
    conflicts: z.object({
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
        items: z.array(membershipConflictSchema).max(20),
    }),
});
export const footballPlayerRelationshipResponseSchema = z.object({
    player: footballPlayerSummarySchema,
    asOf: isoDateSchema,
    effectiveMemberships: z.array(playerTeamMembershipSchema).max(100),
    membershipHistory: z.array(playerTeamMembershipSchema).max(100),
    membershipWindow: z.object({
        limit: z.number().int().positive().max(100),
        effectiveTotal: z.number().int().nonnegative(),
        historyTotal: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
    relationshipSummary: relationshipSummarySchema,
    observations: z.array(playerTeamObservationSchema),
    observationPage: relationshipPageSchema,
    appearances: z.array(fixturePlayerAppearanceSchema),
    appearancePage: relationshipPageSchema,
});
